import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentRegion,
  selectCurrentUser
} from "features/auth/auth-slice";
import { CLINICS_QUERY, useGetClinicsQuery } from "services/clinics";
import { Clinic } from "services/types/clinics.type";

type ClinicFormFieldProps = {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps;
};

export type RhfClinicFormFieldProps<T extends FieldValues> =
  ClinicFormFieldProps & UseControllerProps<T>;

export default function ClinicFormField<T extends FieldValues>({
  control,
  name,
  label = "Clinic",
  required = false,
  disabled = false,
  sx = { ml: 2, mt: 1 }
}: RhfClinicFormFieldProps<T>) {
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentUser = useAppSelector(selectCurrentUser);

  const isShowOwnClinicsOnlyEnabled = currentUser.show_own_clinics_only;

  const {
    data: clinics = [],
    isSuccess,
    isLoading
  } = useGetClinicsQuery(
    {
      query: CLINICS_QUERY,
      region: currentRegion?.id,
      user:
        currentRegion || isShowOwnClinicsOnlyEnabled
          ? currentUser?.id
          : undefined
    },
    {
      skip: !currentUser?.id || currentUser?.id === -1
    }
  );
  if (!isSuccess) {
    return <></>;
  }
  const filterOptions = createFilterOptions({
    stringify: (option: Clinic) => option.name
  });
  return (
    <AutocompleteFormField
      name={name}
      label={label}
      disabled={disabled}
      required={required}
      options={clinics}
      isLoading={isLoading}
      getOptionLabel={(option: Clinic) => option.name}
      filterOptions={filterOptions}
      renderOption={(props: object, option: Clinic) => (
        <ListItem {...props}>
          <ListItemText>{option.name}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option: Clinic, value: Clinic) =>
        option.id === value.id
      }
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      sx={sx}
    />
  );
}
